/**
 * @file usuario.tsx
 */

"use client";

import { LogOut, UserRound } from "lucide-react";
import { AvatarComponent } from "@US";
import { useLogout } from "@login";
import { useUser } from "store";
import Link from "next/link";
import React, { useEffect, useRef, useState } from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Button,
  useSidebar,
} from "visuais";
import { ThemeSwitcher } from "./theme-toggle";

export function UserMenuContent() {
  const logout = useLogout();
  const { userDisplay = "Usuário", isLoading } = useUser();
  const sidebar = useSidebar();
  const [accordionValue, setAccordionValue] = useState<string | undefined>(undefined);
  const rootRef = useRef<HTMLDivElement | null>(null);

  // Fecha automaticamente quando a sidebar é minimizada (colapsada)
  useEffect(() => {
    if (sidebar?.state === "collapsed") {
      setAccordionValue(undefined);
    }
  }, [sidebar?.state]);

  // Observa mudanças de colapso diretamente no DOM da sidebar (CSR only)
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    const sidebarEl = root.closest('[data-collapsible]') as HTMLElement | null;
    if (!sidebarEl) return;

    const closeIfCollapsed = () => {
      const state = sidebarEl.getAttribute('data-state');
      const collapsible = sidebarEl.getAttribute('data-collapsible');
      if (state === 'collapsed' || collapsible === 'icon') {
        setAccordionValue(undefined);
      }
    };

    // Executa apenas no cliente após montar
    closeIfCollapsed();

    const observer = new MutationObserver(() => closeIfCollapsed());
    observer.observe(sidebarEl, { attributes: true, attributeFilter: ['data-state', 'data-collapsible', 'class'] });

    const onMouseLeave = () => setAccordionValue(undefined);
    sidebarEl.addEventListener('mouseleave', onMouseLeave);

    return () => {
      observer.disconnect();
      sidebarEl.removeEventListener('mouseleave', onMouseLeave);
    };
  }, []);

  if (isLoading) return null;

  return (
    <div ref={rootRef}>
      <Accordion
      type="single"
      collapsible
        value={accordionValue}
      onValueChange={setAccordionValue}
      className="bg-neutral-300 hover:bg-neutral-400/80 dark:bg-neutral-700/50 dark:hover:bg-neutral-700/70 rounded-lg rounded-t-2xl shadow-lg shadow-zinc-500/50 
        transition-all transition-500"
    >
      <AccordionItem value="item-1" className="border-b-0">
        <AccordionTrigger className="[&[data-state=open]]:bg-transparent flex items-center gap-2 px-3 py-3 text-left text-sm border-none rounded-t-xl 
          group-data-[collapsible=icon]:px-1 group-data-[collapsible=icon]:py-2 group-data-[collapsible=icon]:justify-center 
          [&>svg]:group-data-[collapsible=icon]:hidden">
          <AvatarComponent className="rounded size-10 group-data-[collapsible=icon]:size-6 group-data-[collapsible=icon]:mx-auto transition-all duration-300" />
          <div className="grid flex-1 text-left text-sm leading-tight group-data-[collapsible=icon]:hidden">
            <span className="truncate font-medium text-white/80 text-xs">
              Bem-vindo,
            </span>
            <span className="truncate font-semibold">{userDisplay}</span>
          </div>
        </AccordionTrigger>
        <AccordionContent>
          <div className="flex flex-col gap-2 mt-1 px-3">
            <div>
              <Button
                asChild
                className="w-full justify-start gap-2"
                variant="outline"
              >
                <Link href="/usuario">
                  <UserRound className="size-4" />
                  <span>Meu Perfil</span>
                </Link>
              </Button>
            </div>
            <div className="flex gap-2 items-center">
              <ThemeSwitcher />
              <div className="flex-1">
                <Button
                  className="w-full flex justify-between items-center rounded-full hover:border-3"
                  variant="destructive"
                  onClick={() => logout()}
                >
                  <span>Sair</span>
                  <LogOut className="size-4" />
                </Button>
              </div>
            </div>
          </div>
        </AccordionContent>
      </AccordionItem>
      </Accordion>
    </div>
  );
}
